<template>
  <div ref="wrapper" :class="['preview-viewer', showLoading && 'loading', isPdfjs && 'pdf-js']">
    <div ref="fingerEl" id="doc-viewer" class="preview-pdf-viewer"></div>
    <van-loading class="pdf-loading" v-if="showLoading"/>
  </div>
</template>
<script>
import AlloyFinger from 'alloyfinger'
import { decrypt } from '@/utils/cryptoHelper'
import debounce from 'lodash/debounce'
import Press from '../mixins/press'
import ClickDelegate from '../../utils/ClickDelegate.js'

import 'pdfjs-dist/es5/web/pdf_viewer.css'

const DEFAULT_SCALE_VALUE = 'auto'
const USE_ONLY_CSS_ZOOM = false // 为 true 时 会影响canvas绘制时画布大小，影响性能
const TEXT_LAYER_MODE = 0 // DISABLE
const MAX_IMAGE_SIZE = -1 // 1024 * 1024
const EVENT_STATE_CHANGED = 'state-changed'

export const PdfState = {
  'PSPDFKIT_SUCCEED': 'PSPDFKIT_SUCCEED',
  'PSPDFKIT_FAILED': 'PSPDFKIT_FAILED',
  'PDFJS_SUCCEED': 'PDFJS_SUCCEED',
  'PDFJS_FAILED': 'PDFJS_FAILED'
}

// pdfjs 接入demo： https://github.com/mozilla/pdf.js/blob/master/examples/mobile-viewer/viewer.js

export default {
  name: 'PdfViewer',
  mixins: [Press],
  props: {
    previewInfo: Object,
    item: Object
  },
  data () {
    return {
      showLoading: false,
      isPdfjs: false,
      progress: 0,
      showProgress: true,
      pageCount: 0,
      pdfjsLib: null,
      eventBus: null,
      pdfLoadingTask: null,
      pdfDocument: null,
      pdfViewer: null,
      pdfHistory: null,
      pdfLinkService: null,
      showBar: false,
      staticUrl: process.env.ASSETS_URL ? process.env.ASSETS_URL : '/',
      defaultScale: 1
    }
  },
  created () {
    this.debouncedDoScale = debounce(this.doScale, 200)
  },
  mounted () {
    const { download_url } = this.previewInfo
    this.showLoading = true
    if (download_url) {
      this.start()
    } else {
      console.error('download_url 不存在')
    }
  },
  beforeDestroy () {
    if (this.isPdfjs) {
      this.close()
    }
  },
  methods: {
    start () {
      let self = this
      import('pdfjs-dist/es5/build/pdf').then(pdfjsLib => {
        self.isPdfjs = !!pdfjsLib
        self.pdfjsLib = pdfjsLib
        self.pdfjsLib.GlobalWorkerOptions.workerSrc = `${self.staticUrl}h5assets/pdf.worker-2.5.207.js`
        self.initUI()
      })
    },
    passwordCallback (cb, code) {
      if (this.previewInfo.password && code === 1) {
        cb(decrypt(this.previewInfo.password))
      }
    },
    getDocumentProgress (data) {
      if (data.loaded > this.loaded) {
        this.loaded = data.loaded
        this.total = data.total
      }
    },
    open () {
      const { download_url } = this.previewInfo
      if (this.pdfLoadingTask) {
        // We need to destroy already opened document
        return this.close().then(() => {
          // ... and repeat the open() call.
          return this.open()
        })
      }
      const loadingTask = this.pdfLoadingTask = this.pdfjsLib.getDocument({
        url: download_url,
        // url: download_url.replace('download01.fangcloud.net', 'mdev.fangcloud.net:8081/download'),
        maxImageSize: MAX_IMAGE_SIZE,
        cMapUrl: `${this.staticUrl}h5assets/cmaps/`,
        cMapPacked: true,
        disableRange: false,
        disableAutoFetch: true
      })
      loadingTask.onPassword = this.passwordCallback
      loadingTask.onProgress = this.getDocumentProgress
      let self = this
      return loadingTask.promise.then(pdfDocument => {
        // Document loaded, specifying document for the viewer.
        this.pdfDocument = pdfDocument
        this.pageCount = pdfDocument.numPages
        this.pdfViewer.setDocument(pdfDocument)
        this.pdfLinkService.setDocument(pdfDocument)

        this.showProgress = false
        this.showLoading = false
        this.autoHideBar()

        this.handlePress(this.$refs.fingerEl, (left, top) => {
          // 向上滑收起
          this.$emit('pressmove', top)
        })

        let that = this
        this.$refs.fingerEl.onclick = ClickDelegate.createListener((count) => {
          if (count === 1) {
            that.$emit('onpageclick')
          }
        })

        this.$emit(EVENT_STATE_CHANGED, PdfState.PDFJS_SUCCEED)
        console.log(`pdf 加载成功`)
      }, function (exception) {
        self.$emit(EVENT_STATE_CHANGED, PdfState.PDFJS_FAILED)
        console.log(exception)
        console.log(`pdf 加载失败`)
      })
    },
    close () {
      if (!this.pdfLoadingTask) {
        return Promise.resolve()
      }

      const promise = this.pdfLoadingTask.destroy()
      this.pdfLoadingTask = null

      if (this.pdfDocument) {
        this.pdfDocument = null
        this.pdfViewer.setDocument(null)
        this.pdfLinkService.setDocument(null, null)
      }

      if (this.pdfHistory) {
        this.pdfHistory.reset()
      }

      return promise
    },
    autoHideBar () {
      this.showBar = true

      if (this.timer) {
        clearTimeout(this.timer)
      }

      this.timer = setTimeout(() => {
        this.showBar = false
      }, 2000)
    },
    initUI () {
      let self = this
      import('pdfjs-dist/es5/web/pdf_viewer.js').then((pdfjsViewer) => {
        var eventBus = new pdfjsViewer.EventBus()
        self.eventBus = eventBus
        const linkService = self.pdfLinkService = new pdfjsViewer.PDFLinkService({
          eventBus: eventBus
        })
        const container = self.$refs.wrapper
        const pdfViewer = self.pdfViewer = new pdfjsViewer.PDFViewer({
          container,
          eventBus,
          linkService,
          useOnlyCssZoom: USE_ONLY_CSS_ZOOM,
          textLayerMode: TEXT_LAYER_MODE
        })
        // 设置初始缩放
        pdfViewer.currentScaleValue = DEFAULT_SCALE_VALUE
        linkService.setViewer(pdfViewer)

        self.pdfHistory = new pdfjsViewer.PDFHistory({
          eventBus: eventBus,
          linkService: linkService
        })

        linkService.setHistory(self.pdfHistory)

        eventBus.on('pagesinit', () => {
          // We can use pdfViewer now, e.g. let's change default scale.
          pdfViewer.currentScaleValue = DEFAULT_SCALE_VALUE
          self.defaultScale = self.pdfViewer.currentScale
        })

        self.open()
        self.initScale()
      })
    },
    initScale () {
      const self = this

      this.finger = new AlloyFinger(this.$refs.fingerEl, {
        pinch: function (evt) {
          self.debouncedDoScale(evt.zoom)
        },
        doubleTap: function () {
          if (self.defaultScale === self.pdfViewer.currentScale) {
            self.pdfViewer.currentScaleValue = self.defaultScale * 2
          } else {
            self.pdfViewer.currentScaleValue = self.defaultScale
          }
        }
      })
    },
    doScale (zoom) {
      this.pdfViewer.currentScaleValue = this.pdfViewer.currentScale * zoom
    }
  }
}
</script>
<style lang="postcss">
.preview-pdf-viewer {
  & .page {
    position: relative;
    margin: 2px auto;
  }
}

.PSPDFKit-Container iframe {
  flex: 1 1 auto;
}
</style>
